import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unwrap, messageOf } from '@truecare/api-client';
import { Alert, Button, Card, CardHeader, Field, Input, MonthInput } from '@truecare/ui';
import { api } from '../api';

/**
 * Phase 17 (change-list 1.2) — Work experience section on the caregiver
 * Profile tab.
 *
 * Replaces the reviewer's original "years of experience" single number with a
 * structured history (Role · Employer · Service length · Comments), plus the
 * résumé upload in the same section.
 *
 * The total shown here is DERIVED server-side from the union of the entries'
 * date ranges (concurrent roles are not double-counted) — it is not a field
 * anyone types. It is also the only part of this section a facility ever sees.
 */

interface WorkEntry {
  id: string;
  roleTitle: string;
  employer: string;
  /** YYYY-MM */
  startedOn: string;
  /** YYYY-MM, or null = current */
  endedOn: string | null;
  comments: string | null;
}

interface DraftEntry {
  roleTitle: string;
  employer: string;
  startedOn: string;
  endedOn: string;
  current: boolean;
  comments: string;
}

const EMPTY_DRAFT: DraftEntry = {
  roleTitle: '',
  employer: '',
  startedOn: '',
  endedOn: '',
  current: false,
  comments: '',
};

const RESUME_ACCEPT =
  'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const RESUME_MAX_BYTES = 10_485_760;

export function CaregiverWorkExperienceSection() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftEntry>(EMPTY_DRAFT);
  const [error, setError] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['caregiver', 'work-experience'],
    queryFn: () => unwrap(api.GET('/api/v1/me/caregiver/work-experience')),
    retry: false,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['caregiver', 'work-experience'] });
  };

  const closeForm = () => {
    setAdding(false);
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
    setError(null);
  };

  const createMutation = useMutation({
    mutationFn: (body: DraftEntry) =>
      unwrap(
        api.POST('/api/v1/me/caregiver/work-experience', { body: toBody(body) }),
      ),
    onSuccess: () => {
      closeForm();
      invalidate();
    },
    onError: (e) => setError(messageOf(e, 'Could not save this entry.')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: DraftEntry }) =>
      unwrap(
        api.PATCH('/api/v1/me/caregiver/work-experience/{id}', {
          params: { path: { id } },
          body: toBody(body),
        }),
      ),
    onSuccess: () => {
      closeForm();
      invalidate();
    },
    onError: (e) => setError(messageOf(e, 'Could not save this entry.')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.DELETE('/api/v1/me/caregiver/work-experience/{id}', {
          params: { path: { id } },
        }),
      ),
    onSuccess: invalidate,
    onError: (e) => setError(messageOf(e, 'Could not remove this entry.')),
  });

  const busy =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  function beginEdit(entry: WorkEntry) {
    setError(null);
    setAdding(false);
    setEditingId(entry.id);
    setDraft({
      roleTitle: entry.roleTitle,
      employer: entry.employer,
      startedOn: entry.startedOn,
      endedOn: entry.endedOn ?? '',
      current: entry.endedOn === null,
      comments: entry.comments ?? '',
    });
  }

  function submitDraft() {
    const problem = validate(draft);
    if (problem !== null) {
      setError(problem);
      return;
    }
    setError(null);
    if (editingId !== null) {
      updateMutation.mutate({ id: editingId, body: draft });
    } else {
      createMutation.mutate(draft);
    }
  }

  const entries: WorkEntry[] = listQuery.data?.items ?? [];
  const totalYears = listQuery.data?.totalYearsExperience ?? 0;

  return (
    <Card padding="lg" accent="left" tone="forest" data-testid="caregiver-work-experience-section">
      <CardHeader
        title="Work experience"
        description="Your care-work history. Facilities see only your total years of experience — never your employers or notes."
        action={
          !adding && editingId === null ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => {
                setError(null);
                setDraft(EMPTY_DRAFT);
                setAdding(true);
              }}
              data-testid="work-experience-add"
            >
              Add role
            </Button>
          ) : undefined
        }
      />

      {listQuery.isPending && (
        <p className="mt-4 font-body text-sm text-ink-muted">Loading your work history…</p>
      )}

      {listQuery.isError && (
        <Alert tone="danger" className="mt-4">
          Couldn't load your work history. Refresh to try again.
        </Alert>
      )}

      {listQuery.data !== undefined && (
        <>
          <p
            className="mt-3 font-body text-sm text-ink"
            data-testid="work-experience-total"
          >
            <strong>{formatTotal(totalYears)}</strong>
            {entries.length > 0 && (
              <span className="text-ink-muted">
                {' '}
                across {entries.length} {entries.length === 1 ? 'role' : 'roles'}
              </span>
            )}
          </p>

          {entries.length === 0 && !adding && (
            <p className="mt-3 font-body text-sm text-ink-muted">
              No roles added yet. Add your care-work history so operators can vet your
              application.
            </p>
          )}

          <ul className="mt-4 flex flex-col divide-y divide-border">
            {entries.map((entry) => (
              <li key={entry.id} className="py-3 first:pt-0 last:pb-0">
                {editingId === entry.id ? (
                  <EntryForm
                    draft={draft}
                    setDraft={setDraft}
                    onSubmit={submitDraft}
                    onCancel={closeForm}
                    busy={busy}
                    submitLabel="Save changes"
                  />
                ) : (
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-body text-sm font-medium text-forest">
                        {entry.roleTitle}
                      </p>
                      <p className="font-body text-sm text-ink">{entry.employer}</p>
                      <p className="mt-0.5 font-body text-xs text-ink-muted">
                        {formatRange(entry.startedOn, entry.endedOn)}
                      </p>
                      {entry.comments !== null && entry.comments !== '' && (
                        <p className="mt-1 font-body text-sm text-ink-muted">
                          {entry.comments}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => beginEdit(entry)}
                        data-testid={`work-experience-edit-${entry.id}`}
                      >
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => deleteMutation.mutate(entry.id)}
                        data-testid={`work-experience-delete-${entry.id}`}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>

          {adding && (
            <div className="mt-4 border-t border-border pt-4">
              <EntryForm
                draft={draft}
                setDraft={setDraft}
                onSubmit={submitDraft}
                onCancel={closeForm}
                busy={busy}
                submitLabel="Add role"
              />
            </div>
          )}

          {error !== null && (
            <Alert tone="danger" className="mt-4" data-testid="work-experience-error">
              {error}
            </Alert>
          )}
        </>
      )}

      <div className="mt-6 border-t border-border pt-4">
        <ResumeUpload />
      </div>
    </Card>
  );
}

// ───────────────────────────── entry form ─────────────────────────────

function EntryForm({
  draft,
  setDraft,
  onSubmit,
  onCancel,
  busy,
  submitLabel,
}: {
  draft: DraftEntry;
  setDraft: (d: DraftEntry) => void;
  onSubmit: () => void;
  onCancel: () => void;
  busy: boolean;
  submitLabel: string;
}) {
  return (
    <div className="flex flex-col gap-3" data-testid="work-experience-form">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Role" id="we-role">
          <Input
            value={draft.roleTitle}
            placeholder="e.g. PSW, Ward Clerk"
            onChange={(e) => setDraft({ ...draft, roleTitle: e.target.value })}
            data-testid="work-experience-role"
          />
        </Field>
        <Field label="Employer" id="we-employer">
          <Input
            value={draft.employer}
            placeholder="e.g. Oakwood Long-Term Care"
            onChange={(e) => setDraft({ ...draft, employer: e.target.value })}
            data-testid="work-experience-employer"
          />
        </Field>
        <Field label="Started" id="we-started" hint="Format: YYYY-MM (e.g. 2026-03).">
          <MonthInput
            value={draft.startedOn}
            onChange={(v) => setDraft({ ...draft, startedOn: v })}
            data-testid="work-experience-started"
          />
        </Field>
        <Field label="Ended" id="we-ended" hint="Format: YYYY-MM. Leave blank if this is your current role.">
          <MonthInput
            value={draft.endedOn}
            disabled={draft.current}
            onChange={(v) => setDraft({ ...draft, endedOn: v })}
            data-testid="work-experience-ended"
          />
        </Field>
      </div>

      <label className="flex items-center gap-2 font-body text-sm text-ink">
        <input
          type="checkbox"
          checked={draft.current}
          onChange={(e) =>
            setDraft({ ...draft, current: e.target.checked, endedOn: '' })
          }
          data-testid="work-experience-current"
        />
        I currently work here
      </label>

      <Field label="Comments" id="we-comments" optional>
        <textarea
          rows={3}
          maxLength={1000}
          value={draft.comments}
          placeholder="Anything worth noting about this role."
          onChange={(e) => setDraft({ ...draft, comments: e.target.value })}
          className="w-full rounded-md border border-border bg-paper px-3 py-2 font-body text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-forest/30"
          data-testid="work-experience-comments"
        />
      </Field>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="primary"
          size="sm"
          disabled={busy}
          onClick={onSubmit}
          data-testid="work-experience-save"
        >
          {submitLabel}
        </Button>
        <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ───────────────────────────── résumé ─────────────────────────────

function ResumeUpload() {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const resumeQuery = useQuery({
    queryKey: ['caregiver', 'resume'],
    queryFn: () => unwrap(api.GET('/api/v1/me/caregiver/resume')),
    retry: false,
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const urlResp = await unwrap(
        api.POST('/api/v1/me/caregiver/resume/upload-url', {
          body: {
            contentType: file.type as
              | 'application/pdf'
              | 'application/msword'
              | 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            contentLength: file.size,
            filename: file.name,
          },
        }),
      );
      const putResp = await fetch(urlResp.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!putResp.ok) {
        throw new Error(
          `Could not upload to storage (HTTP ${String(putResp.status)}). Try again.`,
        );
      }
      return unwrap(
        api.PUT('/api/v1/me/caregiver/resume', {
          body: { key: urlResp.key, filename: file.name },
        }),
      );
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['caregiver', 'resume'] });
    },
    onError: (e) => setError(messageOf(e, 'Upload failed. Try again.')),
  });

  const removeMutation = useMutation({
    mutationFn: () => unwrap(api.DELETE('/api/v1/me/caregiver/resume')),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['caregiver', 'resume'] });
    },
    onError: (e) => setError(messageOf(e, 'Could not remove your résumé. Try again.')),
  });

  const busy = uploadMutation.isPending || removeMutation.isPending;
  const current = resumeQuery.data;
  const hasResume = current?.url != null;

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!RESUME_ACCEPT.split(',').includes(file.type)) {
      setError('Please choose a PDF or Word document (.pdf, .doc, .docx).');
      return;
    }
    if (file.size > RESUME_MAX_BYTES) {
      setError('That file is larger than 10 MB. Please upload a smaller file.');
      return;
    }
    setError(null);
    uploadMutation.mutate(file);
  }

  return (
    <div data-testid="caregiver-resume">
      <p className="font-body text-sm font-medium text-forest">Résumé</p>
      <p className="mt-1 font-body text-xs text-ink-muted">
        PDF or Word document, up to 10 MB. Shared with TrueCare only — facilities never
        see it.
      </p>

      <input
        ref={fileInputRef}
        type="file"
        accept={RESUME_ACCEPT}
        className="hidden"
        onChange={onPick}
        data-testid="resume-input"
      />

      <div className="mt-3 flex flex-wrap items-center gap-3">
        {hasResume && (
          <a
            href={current.url ?? '#'}
            target="_blank"
            rel="noreferrer"
            className="font-body text-sm text-forest underline"
            data-testid="resume-download"
          >
            {current.filename ?? 'Your résumé'}
          </a>
        )}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={() => fileInputRef.current?.click()}
          data-testid="resume-upload"
        >
          {busy ? 'Uploading…' : hasResume ? 'Replace résumé' : 'Upload résumé'}
        </Button>
        {hasResume && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => removeMutation.mutate()}
            data-testid="resume-remove"
          >
            Remove
          </Button>
        )}
      </div>

      {!hasResume && !busy && (
        <p className="mt-2 font-body text-sm text-ink-muted">No résumé on file.</p>
      )}

      {error !== null && (
        <Alert tone="danger" className="mt-3" data-testid="resume-error">
          {error}
        </Alert>
      )}
    </div>
  );
}

// ───────────────────────────── pure helpers ─────────────────────────────

function toBody(d: DraftEntry) {
  return {
    roleTitle: d.roleTitle.trim(),
    employer: d.employer.trim(),
    startedOn: d.startedOn,
    endedOn: d.current || d.endedOn === '' ? null : d.endedOn,
    comments: d.comments.trim() === '' ? null : d.comments.trim(),
  };
}

/** Client-side guard so obvious mistakes don't round-trip to a 400. */
export function validate(d: DraftEntry): string | null {
  if (d.roleTitle.trim() === '') return 'Enter the role you held.';
  if (d.employer.trim() === '') return 'Enter the employer.';
  if (d.startedOn === '') return 'Enter the month you started.';
  if (!d.current && d.endedOn !== '' && d.endedOn < d.startedOn) {
    return 'The end month cannot be before the start month.';
  }
  return null;
}

/** `2020-03` → `Mar 2020`. */
export function formatMonth(month: string): string {
  const [y, m] = month.split('-');
  if (y === undefined || m === undefined) return month;
  const date = new Date(Date.UTC(Number(y), Number(m) - 1, 1));
  return date.toLocaleDateString('en-CA', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** `Mar 2020 – Current` / `Mar 2020 – Jan 2022`. */
export function formatRange(startedOn: string, endedOn: string | null): string {
  return `${formatMonth(startedOn)} – ${endedOn === null ? 'Current' : formatMonth(endedOn)}`;
}

export function formatTotal(years: number): string {
  if (years <= 0) return 'Less than a year of experience';
  return `${String(years)} ${years === 1 ? 'year' : 'years'} of experience`;
}
